import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MODEL_TYPES } from '@/config/models';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';

/**
 * HomePage - Landing page for FinModel
 * Full parity with public/index.html including:
 * - Hero section with living interface mockup
 * - Trust indicators
 * - Problem vs Solution comparison
 * - Interactive How It Works workflow
 * - Business models grid
 * - Features section
 * - CTA section
 */
export function HomePage() {
  const [activeWorkflowStep, setActiveWorkflowStep] = useState(0);

  const workflowSteps = [
    {
      icon: '☑️',
      title: '1. Select Industry',
      desc: 'Choose your model type',
      content: (
        <div className="grid grid-cols-4 gap-4 p-8">
          {MODEL_TYPES.slice(0, 8).map((model, i) => (
            <motion.div
              key={model.id}
              className={`p-6 rounded-xl border-2 cursor-pointer transition-all text-center ${i === 0
                ? 'border-primary-500 bg-primary-50 shadow-lg'
                : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              whileHover={{ scale: 1.02 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <div className="text-3xl mb-3">{model.icon}</div>
              <div className={`font-bold text-sm ${i === 0 ? 'text-primary-700' : 'text-slate-600'}`}>
                {model.name}
              </div>
            </motion.div>
          ))}
        </div>
      ),
    },
    {
      icon: '⚙️',
      title: '2. Input Drivers',
      desc: 'Enter your assumptions',
      content: (
        <div className="p-6 lg:p-8">
          {/* Header with progress indicator */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
                <span className="text-primary-600 font-bold text-lg">⚙️</span>
              </div>
              <div>
                <h4 className="font-bold text-slate-900">Configure Your Model</h4>
                <p className="text-xs text-slate-500">Customize assumptions for your business</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-primary-500 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: '75%' }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
              </div>
              <span className="text-slate-500 font-medium">75%</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Revenue Assumptions */}
            <motion.div
              className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-blue-600">💰</span>
                </div>
                <h4 className="font-bold text-slate-800">Revenue Model</h4>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Starting MRR', value: '$15,000', icon: '📈' },
                  { label: 'Monthly Growth', value: '8%', icon: '🚀' },
                  { label: 'Avg. Contract Value', value: '$299', icon: '💵' },
                ].map((field, j) => (
                  <div key={j} className="flex items-center gap-3 group">
                    <span className="text-sm">{field.icon}</span>
                    <div className="flex-1 text-sm text-slate-600">{field.label}</div>
                    <div className="bg-white px-3 py-1.5 rounded-lg border border-slate-200 group-hover:border-blue-300 transition-colors">
                      <span className="text-sm font-semibold text-slate-800 font-mono">{field.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Cost Structure */}
            <motion.div
              className="bg-gradient-to-br from-emerald-50 to-white rounded-xl p-5 border border-emerald-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <span className="text-emerald-600">📊</span>
                </div>
                <h4 className="font-bold text-slate-800">Cost Structure</h4>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Gross Margin', value: '80%', icon: '📉' },
                  { label: 'CAC per Customer', value: '$450', icon: '🎯' },
                  { label: 'Team Size (Start)', value: '5', icon: '👥' },
                ].map((field, j) => (
                  <div key={j} className="flex items-center gap-3 group">
                    <span className="text-sm">{field.icon}</span>
                    <div className="flex-1 text-sm text-slate-600">{field.label}</div>
                    <div className="bg-white px-3 py-1.5 rounded-lg border border-slate-200 group-hover:border-emerald-300 transition-colors">
                      <span className="text-sm font-semibold text-slate-800 font-mono">{field.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Growth & Retention */}
            <motion.div
              className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-5 border border-purple-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-purple-600">🔄</span>
                </div>
                <h4 className="font-bold text-slate-800">Growth & Retention</h4>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Monthly Churn', value: '2.5%', icon: '📤' },
                  { label: 'Net Revenue Retention', value: '115%', icon: '🔁' },
                  { label: 'Expansion Rate', value: '5%', icon: '⬆️' },
                ].map((field, j) => (
                  <div key={j} className="flex items-center gap-3 group">
                    <span className="text-sm">{field.icon}</span>
                    <div className="flex-1 text-sm text-slate-600">{field.label}</div>
                    <div className="bg-white px-3 py-1.5 rounded-lg border border-slate-200 group-hover:border-purple-300 transition-colors">
                      <span className="text-sm font-semibold text-slate-800 font-mono">{field.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Funding & Capital */}
            <motion.div
              className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-5 border border-amber-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <span className="text-amber-600">🏦</span>
                </div>
                <h4 className="font-bold text-slate-800">Funding & Capital</h4>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Starting Cash', value: '$500K', icon: '💵' },
                  { label: 'Seed Round', value: '$1.5M', icon: '🌱' },
                  { label: 'Target Runway', value: '18 mo', icon: '🛫' },
                ].map((field, j) => (
                  <div key={j} className="flex items-center gap-3 group">
                    <span className="text-sm">{field.icon}</span>
                    <div className="flex-1 text-sm text-slate-600">{field.label}</div>
                    <div className="bg-white px-3 py-1.5 rounded-lg border border-slate-200 group-hover:border-amber-300 transition-colors">
                      <span className="text-sm font-semibold text-slate-800 font-mono">{field.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      ),
    },
    {
      icon: '📊',
      title: '3. Get Strategy',
      desc: 'Export linked Excel',
      content: (
        <div className="p-6 lg:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Results Panel */}
            <motion.div
              className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {/* Excel-like Header */}
              <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-white/30" />
                    <div className="w-3 h-3 rounded-full bg-white/30" />
                    <div className="w-3 h-3 rounded-full bg-white/30" />
                  </div>
                  <span className="text-white text-sm font-medium">📗 SaaS_Financial_Model.xlsx</span>
                </div>
                <div className="text-white/80 text-xs">Last saved: Just now</div>
              </div>

              {/* Tabs */}
              <div className="flex bg-slate-50 border-b border-slate-200 overflow-x-auto">
                {['📋 Summary', '📊 P&L', '⚖️ Balance', '💵 Cash Flow', '📈 Charts', '🎯 KPIs'].map((tab, i) => (
                  <div
                    key={tab}
                    className={`px-4 py-2.5 text-xs font-medium border-r border-slate-200 whitespace-nowrap cursor-pointer transition-colors ${i === 0 ? 'bg-white text-slate-800 border-b-2 border-b-emerald-500' : 'text-slate-500 hover:bg-slate-100'
                      }`}
                  >
                    {tab}
                  </div>
                ))}
              </div>

              {/* Spreadsheet Content */}
              <div className="p-4">
                <div className="border border-slate-200 rounded-lg overflow-hidden text-xs">
                  {/* Header Row */}
                  <div className="grid grid-cols-7 bg-slate-100 font-bold text-slate-700">
                    <div className="p-2.5 border-r border-slate-200">Metric</div>
                    {['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'].map((y) => (
                      <div key={y} className="p-2.5 text-center border-r border-slate-200 last:border-r-0">{y}</div>
                    ))}
                    <div className="p-2.5 text-center bg-emerald-50 text-emerald-700">CAGR</div>
                  </div>
                  {/* Data Rows */}
                  {[
                    { label: '💰 Revenue', values: ['$180K', '$420K', '$780K', '$1.2M', '$1.8M'], cagr: '+78%', highlight: true },
                    { label: '📈 Gross Profit', values: ['$144K', '$336K', '$624K', '$960K', '$1.4M'], cagr: '+77%' },
                    { label: '⚡ EBITDA', values: ['-$36K', '$42K', '$156K', '$360K', '$540K'], cagr: '-' },
                    { label: '✨ Net Income', values: ['-$48K', '$28K', '$117K', '$270K', '$405K'], cagr: '-' },
                    { label: '💵 Cash Balance', values: ['$452K', '$480K', '$597K', '$867K', '$1.27M'], cagr: '+29%' },
                  ].map((row, idx) => (
                    <div
                      key={row.label}
                      className={`grid grid-cols-7 ${row.highlight ? 'bg-emerald-50/50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-blue-50/50 transition-colors`}
                    >
                      <div className="p-2.5 font-medium text-slate-700 border-r border-slate-100">{row.label}</div>
                      {row.values.map((v, i) => (
                        <div
                          key={i}
                          className={`p-2.5 text-right font-mono border-r border-slate-100 ${v.startsWith('-') ? 'text-red-600' : 'text-slate-700'
                            } ${i === 4 ? 'font-bold' : ''}`}
                        >
                          {v}
                        </div>
                      ))}
                      <div className={`p-2.5 text-center font-bold ${row.cagr.startsWith('+') ? 'text-emerald-600 bg-emerald-50' : 'text-slate-500 bg-slate-50'}`}>
                        {row.cagr}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mini Chart */}
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <div className="text-xs font-semibold text-slate-600 mb-3">📈 Revenue Growth Trend</div>
                  <div className="h-16 flex items-end justify-between gap-2">
                    {[18, 42, 78, 120, 180].map((h, i) => (
                      <motion.div
                        key={i}
                        className="flex-1 bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t"
                        initial={{ height: 0 }}
                        animate={{ height: `${(h / 180) * 100}%` }}
                        transition={{ delay: 0.5 + i * 0.1, duration: 0.5 }}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between mt-2 text-[10px] text-slate-500">
                    {['Y1', 'Y2', 'Y3', 'Y4', 'Y5'].map((y) => (
                      <span key={y}>{y}</span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Side Panel - Key Metrics & Download */}
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              {/* Key Metrics Card */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-5 text-white">
                <h4 className="font-bold mb-4 flex items-center gap-2">
                  <span className="text-lg">🎯</span> Key Metrics
                </h4>
                <div className="space-y-3">
                  {[
                    { label: 'Break-even', value: 'Month 14', icon: '⏱️', good: true },
                    { label: 'LTV:CAC Ratio', value: '4.2x', icon: '📊', good: true },
                    { label: 'Runway', value: '24 months', icon: '🛫', good: true },
                    { label: 'Year 5 ARR', value: '$1.8M', icon: '💰', good: true },
                  ].map((metric) => (
                    <div key={metric.label} className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <span>{metric.icon}</span>
                        {metric.label}
                      </div>
                      <div className={`text-sm font-bold ${metric.good ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {metric.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Valuation Card */}
              <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl p-5 text-white">
                <div className="text-sm text-primary-200 mb-1">Projected Valuation (5x Revenue)</div>
                <div className="text-3xl font-bold mb-2">$9.0M</div>
                <div className="flex items-center gap-1 text-xs text-emerald-300">
                  <span>▲</span> 18x return on investment
                </div>
              </div>

              {/* Download Button */}
              <motion.button
                className="w-full bg-emerald-600 text-white px-6 py-4 rounded-xl font-semibold hover:bg-emerald-700 transition-all shadow-lg hover:shadow-emerald-500/25 flex items-center justify-center gap-3"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="text-xl">📥</span>
                <div className="text-left">
                  <div className="font-bold">Download Excel</div>
                  <div className="text-xs text-emerald-200">Fully editable .xlsx file</div>
                </div>
              </motion.button>

              {/* What's Included */}
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="text-xs font-semibold text-slate-700 mb-3">📦 What's Included</div>
                <div className="space-y-2">
                  {['5-Year P&L Forecast', 'Balance Sheet', 'Cash Flow Statement', 'KPI Dashboard', 'Scenario Analysis'].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-xs text-slate-600">
                      <span className="text-emerald-500">✓</span>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans antialiased">
      <Navbar activeLink="home" />

      {/* Hero Section */}
      <section className="bg-white pt-20 pb-24 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              className="text-center lg:text-left"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight text-slate-900">
                Building the{' '}
                <span className="text-primary-600">Intelligent</span> Future of
                Modeling.
              </h1>
              <p className="text-xl text-slate-600 mb-10 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Create detailed, investor-ready financial models with
                industry-specific accuracy. Professional projections with
                perfectly balanced financial statements.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
                <Link
                  to="/dashboard"
                  className="bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-all shadow-lg hover:shadow-primary-500/25 inline-flex items-center justify-center gap-2"
                >
                  Get Started Free
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <a
                  href="#"
                  className="bg-white text-slate-700 border border-slate-200 px-8 py-4 rounded-lg font-semibold hover:bg-slate-50 hover:border-slate-300 transition-colors inline-flex items-center justify-center"
                >
                  Learn More
                </a>
              </div>
              <div className="flex flex-wrap justify-center lg:justify-start gap-12 text-center">
                <div>
                  <div className="text-3xl font-bold text-slate-900">10+</div>
                  <div className="text-slate-500 text-sm font-medium uppercase tracking-wide mt-1">Industries</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-slate-900">100%</div>
                  <div className="text-slate-500 text-sm font-medium uppercase tracking-wide mt-1">Balanced</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-slate-900">Excel</div>
                  <div className="text-slate-500 text-sm font-medium uppercase tracking-wide mt-1">Export Ready</div>
                </div>
              </div>
            </motion.div>

            {/* Living Interface Hero Visual */}
            <motion.div
              className="relative hidden lg:block"
              initial={{ opacity: 0, x: 50, rotateY: 12, rotateX: 6 }}
              animate={{ opacity: 1, x: 0, rotateY: 0, rotateX: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              whileHover={{ rotateY: -2, rotateX: -2 }}
              style={{ perspective: 1000, transformStyle: 'preserve-3d' }}
            >
              {/* Decorative blurs */}
              <div className="absolute -right-12 -bottom-12 w-64 h-64 bg-primary-400/20 rounded-full blur-3xl z-0" />
              <div className="absolute -left-12 -top-12 w-64 h-64 bg-purple-400/20 rounded-full blur-3xl z-0" />

              <div className="bg-white rounded-3xl p-1 shadow-2xl border border-slate-100 relative z-10">
                {/* Glassmorphism overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent rounded-3xl pointer-events-none z-20" />

                {/* Window Controls */}
                <div className="bg-slate-50 border-b border-slate-100 p-4 rounded-t-3xl flex items-center justify-between">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full" />
                    <div className="w-3 h-3 bg-amber-400 rounded-full" />
                    <div className="w-3 h-3 bg-emerald-400 rounded-full" />
                  </div>
                  <div className="flex bg-white border border-slate-200 rounded-md px-3 py-1 items-center gap-2">
                    <span className="text-[10px]">🔒</span>
                    <span className="text-[10px] text-slate-500 font-medium">finmodel.app/dashboard</span>
                  </div>
                </div>

                {/* Mockup Content */}
                <div className="p-6 bg-white rounded-b-3xl min-h-[400px]">
                  {/* Header */}
                  <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white text-sm">📈</div>
                      <div>
                        <div className="text-sm font-bold text-slate-900">SaaS FinModel v2.0</div>
                        <div className="text-[10px] text-slate-400">Autosaved just now</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button className="bg-blue-50 text-blue-600 px-3 py-1 rounded-md text-xs font-medium">Share</button>
                      <button className="bg-slate-900 text-white px-3 py-1 rounded-md text-xs font-medium">Export</button>
                    </div>
                  </div>

                  <div className="flex gap-6">
                    {/* Sidebar */}
                    <div className="w-12 flex flex-col items-center gap-4 py-2 border-r border-slate-100 pr-4">
                      {['🏠', '📊', '📋', '⚙️'].map((icon, i) => (
                        <div
                          key={i}
                          className={`w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer transition-colors ${i === 0 ? 'bg-blue-50 text-blue-600' : 'text-slate-400 hover:bg-slate-50'
                            }`}
                        >
                          {icon}
                        </div>
                      ))}
                    </div>

                    {/* Main Content */}
                    <div className="flex-1">
                      {/* Stats Row */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {[
                          { label: 'MRR Growth', value: '$24.5k', change: '+12%', color: 'green' },
                          { label: 'Runway', value: '18 Mo', change: 'Stable', color: 'amber' },
                          { label: 'Valuation', value: '$4.2M', change: '+8%', color: 'green' },
                        ].map((stat) => (
                          <div key={stat.label} className="p-3 bg-white border border-slate-100 rounded-xl shadow-sm">
                            <div className="text-[10px] text-slate-400 uppercase font-semibold mb-1">{stat.label}</div>
                            <div className="flex items-end justify-between">
                              <div className="text-lg font-bold text-slate-900">{stat.value}</div>
                              <div className={`text-xs font-medium px-1.5 py-0.5 rounded ${stat.color === 'green' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'
                                }`}>
                                {stat.change}
                              </div>
                            </div>
                            {/* Mini Sparkline */}
                            <div className="h-6 mt-2 flex items-end justify-between gap-0.5 opacity-50">
                              {[30, 45, 40, 60, 55, 75, 90].map((h, i) => (
                                <div key={i} className="flex-1 bg-blue-500 rounded-t-sm" style={{ height: `${h}%` }} />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* AI Insight Card */}
                      <div className="p-3 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl shadow-lg text-white mb-4 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-2 opacity-10 text-4xl">✨</div>
                        <div className="relative z-10">
                          <div className="flex items-center gap-1.5 mb-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                            <div className="text-[10px] font-semibold opacity-80">AI Insight</div>
                          </div>
                          <div className="text-xs leading-relaxed opacity-90">
                            "Considering your churn rate of 2.1%, focusing on retention could improve LTV by 15%."
                          </div>
                        </div>
                      </div>

                      {/* Data Grid */}
                      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white text-[11px]">
                        <div className="grid grid-cols-6 font-semibold text-slate-500 bg-slate-50 border-b border-slate-200">
                          <div className="col-span-2 p-2">Metric</div>
                          {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => (
                            <div key={q} className="p-2 text-right">{q}</div>
                          ))}
                        </div>
                        {[
                          { label: 'Revenue', values: ['$120k', '$145k', '$180k', '$215k'], color: 'blue' },
                          { label: 'COGS', values: ['($22k)', '($28k)', '($35k)', '($41k)'], color: 'red' },
                          { label: 'Gross Profit', values: ['$98k', '$117k', '$145k', '$174k'], isBold: true },
                          { label: 'OpEx', values: ['($85k)', '($92k)', '($105k)', '($118k)'], color: 'amber' },
                        ].map((row) => (
                          <div key={row.label} className={`grid grid-cols-6 border-b border-slate-50 ${row.isBold ? 'bg-slate-50/50 font-semibold' : 'hover:bg-slate-50/50'}`}>
                            <div className="col-span-2 p-2 font-medium text-slate-700 flex items-center gap-2">
                              {row.color && <div className={`w-1.5 h-1.5 rounded-full bg-${row.color}-500`} />}
                              {row.label}
                            </div>
                            {row.values.map((v, i) => (
                              <div key={i} className={`p-2 text-right font-mono ${i === 3 ? 'bg-blue-50/30 font-bold' : ''}`}>{v}</div>
                            ))}
                          </div>
                        ))}
                        {/* EBITDA Row */}
                        <div className="grid grid-cols-6 bg-slate-900 text-white font-bold">
                          <div className="col-span-2 p-2 pl-4">EBITDA</div>
                          {['$13k', '$25k', '$40k', '$56k'].map((v, i) => (
                            <div key={i} className={`p-2 text-right font-mono ${i >= 2 ? 'text-green-400' : 'opacity-90'}`}>{v}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Floating Tooltip */}
                  <motion.div
                    className="absolute bottom-24 right-24 bg-white p-3 rounded-lg shadow-xl border border-slate-100 z-30"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <div className="text-xs font-bold text-slate-800">Break-even Point Reached</div>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">Month 8 projected</div>
                    <div className="absolute -bottom-1.5 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-white rotate-45 border-b border-r border-slate-100" />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-10 border-b border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-semibold text-slate-400 uppercase tracking-widest mb-8">
            Trusted by founders building the next big thing
          </p>
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
            {[
              { icon: '✓', name: 'TechStarters' },
              { icon: '🚀', name: 'VentureScale' },
              { icon: '📊', name: 'AlphaFund' },
              { icon: '🏦', name: 'SeedCapital' },
            ].map((company) => (
              <div key={company.name} className="flex items-center gap-2">
                <span className="text-2xl text-slate-800">{company.icon}</span>
                <span className="font-bold text-xl text-slate-700">{company.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem vs Solution */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Stop wrestling with broken spreadsheets
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Most financial models are fragile, hard to update, and prone to errors. We fixed that.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {/* The Old Way */}
            <motion.div
              className="p-8 rounded-2xl bg-slate-50 border border-slate-100 relative overflow-hidden group"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity text-9xl">
                ❌
              </div>
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600">✗</div>
                  <h3 className="text-xl font-bold text-slate-900">The Old Way</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    'Broken formulas whenever you add a row',
                    'Manual formatting takes hours',
                    'Balance sheet never balances',
                    'Hardcoded numbers hidden in cells',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="text-red-400 mt-1">❌</span>
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* The FinModel Way */}
            <motion.div
              className="p-8 rounded-2xl bg-white border border-primary-100 shadow-xl shadow-primary-900/5 relative overflow-hidden group"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 to-transparent" />
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity text-9xl">
                ✅
              </div>
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600">✓</div>
                  <h3 className="text-xl font-bold text-slate-900">The FinModel Way</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    'Standardized, audit-ready logic',
                    'Investor-grade formatting automatically',
                    '100% Balanced statements, guaranteed',
                    'Clean inputs separated from calculations',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="text-primary-500 mt-1">✅</span>
                      <span className="text-slate-700 font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-slate-50 border-t border-slate-100 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[100px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">How it Works</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From idea to investor-ready financial model in three simple steps.
            </p>
          </motion.div>

          <div className="w-full max-w-5xl mx-auto">
            {/* Tabs */}
            <div className="flex gap-4 mb-8">
              {workflowSteps.map((step, index) => (
                <button
                  key={index}
                  onClick={() => setActiveWorkflowStep(index)}
                  className={`flex-1 p-6 rounded-xl border-2 transition-all text-left ${activeWorkflowStep === index
                    ? 'border-primary-500 bg-white shadow-lg'
                    : 'border-slate-200 bg-white/50 hover:border-slate-300'
                    }`}
                >
                  <div className={`text-3xl mb-3 ${activeWorkflowStep === index ? '' : 'grayscale opacity-50'}`}>
                    {step.icon}
                  </div>
                  <h3 className={`font-bold ${activeWorkflowStep === index ? 'text-slate-900' : 'text-slate-600'}`}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-500">{step.desc}</p>
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg min-h-[400px] overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeWorkflowStep}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {workflowSteps[activeWorkflowStep].content}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* Business Models Section */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Industry-Specific Financial Models
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose from our library of professional templates, each with tailored assumptions and metrics.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {MODEL_TYPES.map((model, index) => (
              <motion.div
                key={model.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/model/${model.id}`}
                  className="block bg-white p-6 rounded-xl border border-slate-200 hover:border-primary-300 hover:shadow-lg transition-all group"
                >
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${model.color}15` }}
                  >
                    {model.icon}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{model.name}</h3>
                  <p className="text-sm text-slate-500">{model.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors"
            >
              View All Business Models
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Enterprise-Grade Features, Simplified
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Professional financial modeling tools that used to cost thousands.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '📊', title: 'One-Click Excel Export', description: 'Download fully functional, unlocked .xlsx files with all formulas preserved.', color: 'blue' },
              { icon: '⚖️', title: '100% Balanced Statements', description: 'Double-entry accounting ensures P&L, Balance Sheet, and Cash Flow always tie out.', color: 'green' },
              { icon: '🧠', title: 'AI-Driven Insights', description: 'Automated analysis highlights risks, opportunities, and industry benchmarks.', color: 'purple' },
              { icon: '📈', title: 'Industry-Specific Models', description: 'SaaS, E-commerce, Marketplace, and more—each with tailored drivers and KPIs.', color: 'amber' },
              { icon: '🎯', title: 'Investor-Ready Charts', description: 'Beautiful, auto-generated charts ready for your pitch deck.', color: 'red' },
              { icon: '🔒', title: 'Secure & Private', description: 'Bank-grade encryption. Your financial data is never shared.', color: 'indigo' },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                className="p-8 rounded-2xl bg-white border border-slate-100 shadow-lg hover:shadow-xl transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center text-3xl mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/features"
              className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors"
            >
              Explore All Features
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Build Your Financial Model?
            </h2>
            <p className="text-slate-300 text-lg mb-8">
              Join thousands of founders who trust FinModel to plan their financial future.
            </p>
            <Link
              to="/dashboard"
              className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-bold py-4 px-10 rounded-lg transition-all shadow-lg hover:shadow-primary-600/30 transform hover:-translate-y-1"
            >
              Start Building for Free
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
